import { Monitor, Moon, Sun } from 'lucide-react';
import { MODE_OPTIONS, THEME_OPTIONS, nextTheme, themeLabel } from '../lib/theme.ts';
import type { ThemeMode, ThemeName } from '../lib/theme.ts';
import { IconButton } from './IconButton.tsx';
import { Segmented } from './Segmented.tsx';

const ICON: Record<ThemeName, typeof Sun> = { dark: Moon, light: Sun, 'high-contrast': Moon };
const MODE_ICON: Record<ThemeMode, typeof Sun> = { dark: Moon, light: Sun, system: Monitor };

export interface ThemeToggleProps {
  theme: ThemeName;
  onChange: (t: ThemeName) => void;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

/** One button that switches between dark and light. Shows the theme it switches to. */
export function ThemeToggle({ theme, onChange, tooltipSide }: ThemeToggleProps) {
  const next = nextTheme(theme);
  const Icon = ICON[next];
  return (
    <IconButton
      label={`Switch to ${themeLabel(next).toLowerCase()} theme`}
      icon={<Icon />}
      tooltipSide={tooltipSide}
      data-theme-current={theme}
      onClick={() => onChange(next)}
    />
  );
}

/** Dark and light side by side. */
export function ThemePicker({ theme, onChange }: { theme: ThemeName; onChange: (t: ThemeName) => void }) {
  return (
    <Segmented
      label="Theme"
      value={theme}
      onChange={onChange}
      options={THEME_OPTIONS.map((o) => {
        const Icon = ICON[o.value];
        return { value: o.value, label: o.label, icon: <Icon /> };
      })}
    />
  );
}

export interface AppearancePickerProps {
  mode: ThemeMode;
  onMode: (m: ThemeMode) => void;
  className?: string;
}

/** Light, dark or system. The accent is the brand's own Prism blue, one colour everywhere (ADR 0061). */
export function AppearancePicker({ mode, onMode, className }: AppearancePickerProps) {
  return (
    <Segmented
      label="Theme"
      value={mode}
      onChange={onMode}
      className={className}
      options={MODE_OPTIONS.map((o) => {
        const Icon = MODE_ICON[o.value];
        return { value: o.value, label: o.label, icon: <Icon /> };
      })}
    />
  );
}
